import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowForward,
} from '@mui/icons-material';
import { PhaseDefinition, TransitionDefinition } from '../../types';

interface TransitionLogicEditorProps {
  phases: PhaseDefinition[];
  transitions: TransitionDefinition[];
  onAddTransition: (transition: TransitionDefinition) => void;
  onUpdateTransition: (transitionId: number, transition: TransitionDefinition) => void;
  onDeleteTransition: (transitionId: number) => void;
}

const TransitionLogicEditor: React.FC<TransitionLogicEditorProps> = ({
  phases,
  transitions,
  onAddTransition,
  onUpdateTransition,
  onDeleteTransition,
}) => {
  const [fromPhaseId, setFromPhaseId] = useState<number | ''>('');
  const [toPhaseId, setToPhaseId] = useState<number | ''>('');
  const [condition, setCondition] = useState('');
  const [editingTransitionId, setEditingTransitionId] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleAddClick = () => {
    setEditingTransitionId(null);
    setFromPhaseId('');
    setToPhaseId('');
    setCondition('');
    setErrors({});
  };

  const handleEditClick = (transition: TransitionDefinition) => {
    setEditingTransitionId(transition.transitionId);
    setFromPhaseId(transition.fromPhaseId);
    setToPhaseId(transition.toPhaseId);
    setCondition(transition.condition || '');
    setErrors({});
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!fromPhaseId) {
      newErrors.fromPhaseId = 'From phase is required';
    }

    if (!toPhaseId) {
      newErrors.toPhaseId = 'To phase is required';
    }

    if (fromPhaseId && toPhaseId && fromPhaseId === toPhaseId) {
      newErrors.toPhaseId = 'Cannot transition to the same phase';
    }

    // Check if phases exist
    if (fromPhaseId && typeof fromPhaseId === 'number' && !phases.find(p => p.phaseId === fromPhaseId)) {
      newErrors.fromPhaseId = 'Invalid from phase';
    }

    if (toPhaseId && typeof toPhaseId === 'number' && !phases.find(p => p.phaseId === toPhaseId)) {
      newErrors.toPhaseId = 'Invalid to phase';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    if (typeof fromPhaseId !== 'number' || typeof toPhaseId !== 'number') {
      return; // Validation should have caught this
    }

    const transitionData: TransitionDefinition = {
      transitionId: editingTransitionId || Date.now(),
      fromPhaseId,
      toPhaseId,
      condition: condition.trim() || undefined,
    };

    if (editingTransitionId) {
      onUpdateTransition(editingTransitionId, transitionData);
    } else {
      onAddTransition(transitionData);
    }

    // Reset form
    setFromPhaseId('');
    setToPhaseId('');
    setCondition('');
    setEditingTransitionId(null);
    setErrors({});
  };

  const handleCancel = () => {
    setFromPhaseId('');
    setToPhaseId('');
    setCondition('');
    setEditingTransitionId(null);
    setErrors({});
  };

  const handleDelete = (transitionId: number) => {
    if (window.confirm('Are you sure you want to delete this transition?')) {
      onDeleteTransition(transitionId);
    }
  };

  const getPhaseName = (phaseId: number): string => {
    const phase = phases.find(p => p.phaseId === phaseId);
    return phase ? phase.name : phaseId.toString();
  };

  if (phases.length < 2) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="info">
          You need at least 2 phases to create transitions between them.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Transition Logic
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define how phases transition to each other. Transitions can include optional conditions.
      </Typography>

      {/* Add/Edit Form */}
      <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        {editingTransitionId ? (
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Editing Transition
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2">Add New Transition</Typography>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={handleAddClick}
              variant="outlined"
            >
              New Transition
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth error={!!errors.fromPhaseId}>
              <InputLabel>From Phase</InputLabel>
              <Select
                value={fromPhaseId}
                onChange={(e) => {
                  setFromPhaseId(e.target.value === '' ? '' : Number(e.target.value));
                  if (errors.fromPhaseId) {
                    setErrors({ ...errors, fromPhaseId: '' });
                  }
                }}
                label="From Phase"
              >
                {phases.map((phase) => (
                  <MenuItem key={phase.phaseId} value={phase.phaseId}>
                    {phase.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.fromPhaseId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.fromPhaseId}
                </Typography>
              )}
            </FormControl>

            <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
              <ArrowForward color="action" />
            </Box>

            <FormControl fullWidth error={!!errors.toPhaseId}>
              <InputLabel>To Phase</InputLabel>
              <Select
                value={toPhaseId}
                onChange={(e) => {
                  setToPhaseId(e.target.value === '' ? '' : Number(e.target.value));
                  if (errors.toPhaseId) {
                    setErrors({ ...errors, toPhaseId: '' });
                  }
                }}
                label="To Phase"
              >
                {phases.map((phase) => (
                  <MenuItem key={phase.phaseId} value={phase.phaseId}>
                    {phase.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.toPhaseId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.toPhaseId}
                </Typography>
              )}
            </FormControl>
          </Box>

          <TextField
            label="Condition (Optional)"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Optional condition for this transition (e.g., 'If approval is granted')"
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!fromPhaseId || !toPhaseId}
            >
              {editingTransitionId ? 'Update' : 'Add'} Transition
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Existing Transitions List */}
      {transitions.length === 0 ? (
        <Alert severity="info">
          No transitions defined yet. Add transitions to define how phases flow into each other.
        </Alert>
      ) : (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Existing Transitions ({transitions.length})
          </Typography>
          <List>
            {transitions.map((transition) => (
              <ListItem
                key={transition.transitionId}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: editingTransitionId === transition.transitionId ? '#e3f2fd' : 'white',
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={getPhaseName(transition.fromPhaseId)} size="small" />
                      <ArrowForward fontSize="small" color="action" />
                      <Chip label={getPhaseName(transition.toPhaseId)} size="small" color="primary" />
                      {transition.condition && (
                        <Chip
                          label={transition.condition}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  }
                />
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEditClick(transition)}
                    title="Edit"
                    color="primary"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(transition.transitionId)}
                    title="Delete"
                    color="error"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Paper>
  );
};

export default TransitionLogicEditor;

